import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import { Member } from '../member/model'
import { Trainer, TrainerService } from '../user/model'
import {
  BranchAccessUser,
  BranchAuditEntry,
  Staff,
  StaffAccountStatusFilter,
  StaffActivityLog,
  StaffPendingWorkItem,
  StaffStatusEvent,
  TrainerClassHistory,
  TrainerGoalSetting,
  TrainerPrivateTrainingHistory,
  TrainerSchedule,
  WorkingDay,
} from './model'

type PaginatedApiResponse<T = Record<string, unknown>> = {
  data: T[]
  pagination: { total: number; page: number; pageSize: number }
}

export const StaffService = (client: AxiosInstance) => {
  return {
    async getStaffs(
      opt: PaginationOpt & { presetCodes?: string; accountStatus?: StaffAccountStatusFilter }
    ): Promise<PaginationResponse<Staff>> {
      const { data } = await wrapApiCall(() =>
        client.get<PaginatedApiResponse>('/users/staff', { params: opt })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: (data?.data ?? []).map((item) => deserialize(item, Staff)),
      }
    },
    async createStaff(staff: Staff, opts?: { branchIdOverride?: number }): Promise<Staff> {
      // Multi-branch tenant: form picker value overrides the global axios
      // `branch-id` header for this single request. BE writes the new staff's
      // user_branches row against this branch (or all branches for Club Owner).
      // Mirrors PackageService.createPackage.
      const config =
        opts?.branchIdOverride != null
          ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
          : undefined
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/users/staff', staff.serializeForCreate(), config)
      )
      return deserialize(data, Staff)
    },
    async getTrainerWorkingDays(id: string | number): Promise<WorkingDay[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/trainer/${id}/working-days`)
      )
      return (data ?? []).map((item) => deserialize(item, WorkingDay))
    },
    async getTrainerMySchedules(
      startTime: string,
      endTime: string,
      trainerId?: number,
      includeCancelled?: boolean
    ): Promise<TrainerSchedule[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>(
          `/trainer/schedule`,
          {
            params: {
              trainerId: trainerId ? trainerId : undefined,
              startTime,
              endTime,
              includeCancelled,
            },
          }
        )
      )
      const payload: Record<string, unknown>[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? (data as { data: Record<string, unknown>[] }).data
          : []
      return payload.map((item) => {
        const memberRaw = (item as { member?: unknown }).member
        const normalizedMember = Array.isArray(memberRaw)
          ? memberRaw.map((m: Record<string, unknown>) => deserialize(m, Member))
          : memberRaw
            ? deserialize(memberRaw as Record<string, unknown>, Member)
            : undefined

        const schedule = deserialize(item, TrainerSchedule)
        ;(schedule as { member?: unknown }).member = normalizedMember
        return schedule
      })
    },
    async getTrainerSchedules(
      id: string | number,
      startTime: string,
      endTime: string,
      includeCancelled?: boolean,
      classId?: number
    ): Promise<TrainerSchedule[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>(
          `/trainer/${id}/my-schedules`,
          {
            params: { startTime, endTime, includeCancelled, classId },
          }
        )
      )
      const payload: Record<string, unknown>[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? (data as { data: Record<string, unknown>[] }).data
          : []
      return payload.map((item) => {
        const memberRaw = (item as { member?: unknown }).member
        const normalizedMember = Array.isArray(memberRaw)
          ? memberRaw.map((m: Record<string, unknown>) => deserialize(m, Member))
          : memberRaw
            ? deserialize(memberRaw as Record<string, unknown>, Member)
            : undefined

        const schedule = deserialize(item, TrainerSchedule)
        ;(schedule as { member?: unknown }).member = normalizedMember
        return schedule
      })
    },
    async getTrainerClassHistory(
      id: string | number,
      params?: Record<string, unknown>
    ): Promise<PaginationResponse<TrainerClassHistory>> {
      const { data } = await wrapApiCall(() =>
        client.get<PaginatedApiResponse>(`/trainer/${id}/class-history`, { params })
      )

      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: (data?.data ?? []).map((item) => deserialize(item, TrainerClassHistory)),
      }
    },
    async getTrainerPrivateTrainingHistory(
      id: string | number,
      params?: Record<string, unknown>
    ): Promise<PaginationResponse<TrainerPrivateTrainingHistory>> {
      const { data } = await wrapApiCall(() =>
        client.get<PaginatedApiResponse>(`/trainer/${id}/pt-history`, { params })
      )

      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: (data?.data ?? []).map((item) => deserialize(item, TrainerPrivateTrainingHistory)),
      }
    },
    async getTrainerGoalSettings(
      id: string | number,
      params?: Record<string, unknown>
    ): Promise<PaginationResponse<TrainerGoalSetting>> {
      const { data } = await wrapApiCall(() =>
        client.get<PaginatedApiResponse>(`/trainer/${id}/goal-settings`, { params })
      )

      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: (data?.data ?? []).map((item) => deserialize(item, TrainerGoalSetting)),
      }
    },
    async updateTrainerWorkingDays(
      id: string | number,
      workingDays: WorkingDay[]
    ): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(
          `/trainer/${id}/working-days`,
          workingDays.map((d) => d.serializeForCreate())
        )
      )
      return !!data?.success
    },
    async updateTrainingServices(
      id: string | number,
      services: TrainerService[]
    ): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.patch<{ success?: boolean }>(
          `/trainer/${id}/service`,
          services.map((s) => s.serializeForCreate())
        )
      )
      return !!data?.success
    },
    async createTrainerProfile(
      id: string | number,
      trainer: Trainer,
      workingDays: WorkingDay[],
      services: TrainerService[]
    ): Promise<Trainer> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>(`/trainer/profile/${id}`, {
          ...trainer.serializeForCreate(),
          workingDays: workingDays.map((d) => d.serializeForCreate()),
          trainerServices: services.map((s) => s.serializeForCreate()),
        })
      )
      return deserialize(data, Trainer)
    },
    async editTrainerProfile(id: string | number, trainer: Trainer): Promise<Trainer> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(
          `/trainer/profile/${id}`,
          trainer.serializeForCreate()
        )
      )
      return deserialize(data, Trainer)
    },
    async removeStaff(id: string | number): Promise<void> {
      await wrapApiCall(() => client.delete(`/users/${id}`))
    },
    /**
     * QTY-2041: work still assigned to this staff member that has to be moved
     * before they can be set In-Active. Empty array = nothing blocking.
     *
     * The server re-checks this inside `deactivateStaff` too, so a dialog left
     * open while new work lands cannot slip a deactivation through.
     */
    async getStaffPendingWork(id: string | number): Promise<StaffPendingWorkItem[]> {
      const { data } = await wrapApiCall(() =>
        client.get<{ items?: StaffPendingWorkItem[] } | StaffPendingWorkItem[]>(
          `/users/staff/${id}/pending-work`
        )
      )
      return Array.isArray(data) ? data : (data?.items ?? [])
    },
    /**
     * QTY-2041: soft-delete. Flips the existing record to INACTIVE — it never
     * creates or removes a document, so history and identity stay intact and
     * `reactivateStaff` can bring the same person back.
     */
    async deactivateStaff(
      id: string | number,
      body: { reason: string; effectiveDate: string }
    ): Promise<void> {
      await wrapApiCall(() => client.post(`/users/${id}/deactivate`, body))
    },
    /** QTY-2043: rehire — flips the same record back to ACTIVE. Idempotent. */
    async reactivateStaff(id: string | number): Promise<void> {
      await wrapApiCall(() => client.post(`/users/${id}/reactivate`))
    },
    /** QTY-2043: full deactivate/reactivate timeline (can span several stints). */
    async getStaffStatusHistory(id: string | number): Promise<StaffStatusEvent[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/users/staff/${id}/status-history`)
      )
      return (data ?? []).map((item) => deserialize(item, StaffStatusEvent))
    },
    async editStaff(id: string | number, staff: Staff): Promise<Staff> {
      const { data } = await wrapApiCall(() =>
        client.patch<Record<string, unknown>>(`/users/${id}`, staff.serializeForCreate())
      )
      return deserialize(data, Staff)
    },
    async resetPassword(id: string | number): Promise<void> {
      await wrapApiCall(() => client.post(`/users/${id}/reset-password`))
    },
    async assignPresets(id: string | number, presetCodes: string[]): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(`/users/${id}/assign-preset`, { presetCodes })
      )
      return !!data?.success
    },
    async uploadImage(id: string | number, file: File): Promise<boolean> {
      const formData = new FormData()
      formData.append('image', file)

      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(`/users/${id}/profile-picture`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      )
      return !!data?.success
    },
    async uploadTrainerImage(id: string | number, file: File): Promise<boolean> {
      const formData = new FormData()
      formData.append('image', file)

      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(`/trainer/${id}/profile-picture`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      )
      return !!data?.success
    },
    async getStaffActivityLogs(
      opt: PaginationOpt & { staffId?: string | number; yearMonth?: string }
    ): Promise<PaginationResponse<StaffActivityLog>> {
      const params = {
        page: opt.page,
        pageSize: opt.pageSize,
        query: opt.query,
        staffId: opt.staffId,
        yearMonth: opt.yearMonth,
      }
      const { data } = await wrapApiCall(() =>
        client.get<PaginatedApiResponse>('/users/staff/activity-logs', { params })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: (data?.data ?? []).map((item) => deserialize(item, StaffActivityLog)),
      }
    },
    async assignBranchesToStaffs(
      userIds: number[],
      branchIds: number[]
    ): Promise<{ success: boolean; assigned: number; staffCount: number; branchCount: number }> {
      const { data } = await wrapApiCall(() =>
        client.post<{
          success: boolean
          assigned: number
          staffCount: number
          branchCount: number
        }>('/users/staff/assign-branches', { userIds, branchIds })
      )
      return data
    },
    async getBranchAccessUsers(params: {
      page?: number
      pageSize?: number
      query?: string
      roleId?: number
      scope?: 'all' | 'all-branches' | 'multi' | 'single'
      branchId?: number
    }): Promise<{
      data: BranchAccessUser[]
      pagination: { page: number; pageSize: number; total: number }
      totalBranches: number
    }> {
      const cleaned = Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined)
      )
      const { data } = await wrapApiCall(() =>
        client.get<{
          data: BranchAccessUser[]
          pagination: { page: number; pageSize: number; total: number }
          totalBranches: number
        }>('/users/staff/branch-access', { params: cleaned })
      )
      return data
    },
    async assignUserBranches(id: number | string, branchIds: number[]): Promise<void> {
      // Additive grant (POST) — unlike setUserBranches (PUT) it doesn't
      // replace the existing set and isn't gated on the staff-list UPDATE
      // permission, so it's safe right after create-staff.
      await wrapApiCall(() => client.post(`/users/${id}/branches`, { branchIds }))
    },
    async setUserBranches(
      id: number,
      branchIds: number[]
    ): Promise<{ success: boolean; branchCount: number }> {
      const { data } = await wrapApiCall(() =>
        client.put<{ success: boolean; branchCount: number }>(`/users/${id}/branches`, {
          branchIds,
        })
      )
      return data
    },
    async removeBranchesFromStaffs(
      userIds: number[],
      branchIds: number[]
    ): Promise<{ success: boolean; removed: number; skipped: { id: number; name: string }[] }> {
      const { data } = await wrapApiCall(() =>
        client.post<{
          success: boolean
          removed: number
          skipped: { id: number; name: string }[]
        }>('/users/staff/remove-branches', { userIds, branchIds })
      )
      return data
    },
    async getUserBranchAudit(id: number, take = 20): Promise<BranchAuditEntry[]> {
      const { data } = await wrapApiCall(() =>
        client.get<BranchAuditEntry[]>(`/users/${id}/branch-audit`, { params: { take } })
      )
      return data ?? []
    },
    async getStaffBranches(id: string | number): Promise<{ id: number; name: string }[]> {
      const { data } = await wrapApiCall(() =>
        client.get<{ id: number; name: string }[]>(`/users/${id}/branches`)
      )
      return data ?? []
    },
    async staffOption(): Promise<{
      genders: string[]
      relationship: string[]
      addressType: string[]
    }> {
      const { data } = await wrapApiCall(() =>
        client.get<{ genders: string[]; relationship: string[]; addressType: string[] }>(
          '/users/staff/options'
        )
      )
      return data
    },
  }
}
